import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { FilterProvider } from './context/DataAndFilterContext';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { GlobalLoadingProvider, useGlobalLoading } from './context/GlobalLoadingContext';
import CircularProgressWithLabel from './components/CircularProgressWithLabel';
import MarketingPage from './marketing/MarketingPage';
import ContributorPage from './marketing/ContributorPage';
import LoginPage from './LoginPage';
import SignupPage from './SignupPage';
import ForgotPasswordPage from './ForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import PostLoginPage from './Dashboard';
import Dashboard from './DashboardNew';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import ManageData from './ManageData';
import PersonaProfiles from './PersonaProfiles';
import PersonaProfile from './PersonaProfile';
import CustomerConversationsPage from './CustomerConversationsPage';
import ChosenSolutionsPage from './ChosenSolutionsPage';
import InsightCategoryPage from './components/insights/InsightCategoryPage';
import TriggersPage from './TriggersPage';
import PreviousSolutionsPage from './PreviousSolutionsPage';
import ConsideredSolutionsPage from './ConsideredSolutionsPage';
import CustomerNeedsPage from './CustomerNeedsPage';
import UsageInsightsPage from './UsageInsightsPage';
import InsightDetails from './InsightDetails';
import IntegrationsPage from './IntegrationsPage';
import IntegrationSetup from './IntegrationSetup';
import IntegrationAlerts from './IntegrationAlerts';
import EditIntegration from './IntegrationEdit';
import Wizard from './Wizard';
import TrainingCategorizationFeedbackLoopInterface from './TrainingCategorizationFeedbackLoopInterface';
import ConversationReview from './TrainingConversationReview';
import TrainingActivity from './TrainingActivity';
import StrategicReport from './StrategicReport';
import JobMapQA from './JobMapQA';
import PreviousSolutionsAndTriggers from './PreviousSolutionsAndTriggers';
import JobDetailPage from './pages/JobDetailPage';
import ProductDetailPage from './pages/ProductDetailPage';
import { DashboardDataProvider } from './context/DashboardDataContext';
import { TableVisibilityProvider } from './context/TableVisibilityContext';
import { InterviewsListAdmin } from "./AdminInterviewsList";
import InterviewPage from "./InterviewPage";
import { InterviewTypesAdmin } from "./AdminInterviewTypes";
import InterviewScriptsAdmin from "./AdminInterviewScripts";
import { InterviewViewerPage } from "./InterviewViewerPage";
import InterviewsPage from "./InterviewsPage";
import InterviewLandingPage from "./InterviewLandingPage";
import Admin from './AdminUsers';
import OrganizationsAdmin from './AdminOrganizations';
import IndustriesAdmin from './AdminIndustries';
import ProductsAdmin from './AdminProducts';
import AdminFunnel from './AdminFunnel';
import SubscriptionsOverview from './pages/SubscriptionsOverview';
import Contact from './pages/Contact';
import { AuthProvider } from './context/AuthContext';
 

const LoadingOverlay = ({ children }) => {
  const { loading } = useGlobalLoading();

  return (
    <>
      {children}
      {loading && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <h2>Loading your organization's conversations...</h2>
          <CircularProgressWithLabel value={100} />
        </div>
      )}
    </>
  );
};

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <GlobalLoadingProvider> {/* Wrap the app with global loading provider to show loading progress spinner while data loads */}
        <AuthProvider>
          <FilterProvider>
            <DashboardDataProvider>
              <TableVisibilityProvider defaultOn={false}>
                <Router>
                  <Routes>

                    {/* New AI Interview tool Routes */}
                    <Route path="/participate/:slug" element={<InterviewLandingPage />} /> {/* The landing page to start the interview */}
                    <Route path="/interview/:interviewId" element={<InterviewPage />} /> {/* The actual page to do the interview */}

                    {/* Marketing Routes — public, and deliberately outside PublicRoute,
                        which redirects logged-in users away. Prospects must be able to
                        reach /contact without an account. */}
                    <Route path="/" element={<MarketingPage />} />
                    <Route path="/contributors" element={<ContributorPage />} />
                    <Route path="/contact" element={<Contact />} />

                    {/* Design lab — prototypes only, not linked from the live page. */}

                    {/* Public routes - redirects to a page defined in PublicRoute if logged in */}
                    <Route path="/" element={<PublicRoute />}>
                      <Route path="login" element={<LoginPage />} />
                      <Route path="signup" element={<SignupPage />} />
                      <Route path="forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="reset-password/:token" element={<ResetPasswordPage />} />
                    </Route>

                    {/* Private routes - redirects to login if not logged in */}
                    <Route path="/" element={<PrivateRoute />}>
                      <Route path="wizard" element={<LoadingOverlay><Wizard /></LoadingOverlay>} />
                      <Route path="dashboard-old" element={<LoadingOverlay><PostLoginPage /></LoadingOverlay>} />
                      <Route path="dashboard" element={<LoadingOverlay><Dashboard /></LoadingOverlay>} />
                      <Route path="conversations" element={<LoadingOverlay><CustomerConversationsPage /></LoadingOverlay>} />
                      <Route path="chosen-solutions" element={<LoadingOverlay><ChosenSolutionsPage /></LoadingOverlay>} />
                      <Route path="triggers" element={<LoadingOverlay><TriggersPage /></LoadingOverlay>} />
                      <Route path="previous-solutions" element={<LoadingOverlay><PreviousSolutionsPage /></LoadingOverlay>} />
                      <Route path="previous-solutions-and-triggers" element={<LoadingOverlay><PreviousSolutionsAndTriggers /></LoadingOverlay>} />
                      <Route path="considered-solutions" element={<LoadingOverlay><ConsideredSolutionsPage /></LoadingOverlay>} />
                      <Route path="customer-needs" element={<LoadingOverlay><CustomerNeedsPage /></LoadingOverlay>} />
                      <Route path="usage-insights" element={<LoadingOverlay><UsageInsightsPage /></LoadingOverlay>} />
                      <Route path="/subscriptions" element={<SubscriptionsOverview />} />

                      {/* NEW: drill-in page used by TopInsightsByType and InsightTrendsTable */}
                      <Route path="/insights/:category" element={<LoadingOverlay><InsightCategoryPage /></LoadingOverlay>} />
                      <Route path="/insight/:category" element={<InsightCategoryPage />} />
                      <Route path="personas" element={<LoadingOverlay><PersonaProfiles /></LoadingOverlay>} />
                      <Route path="job-map" element={<LoadingOverlay><JobMapQA /></LoadingOverlay>} />
                      <Route path="persona/:persona_id" element={<LoadingOverlay><PersonaProfile /></LoadingOverlay>} />
                      <Route path="managedata" element={<LoadingOverlay><ManageData /></LoadingOverlay>} />
                      <Route path="strategic-report" element={<LoadingOverlay><StrategicReport /></LoadingOverlay>} />

                      {/* New entity pages */}                
                      <Route path="job/:jobSlug" element={<LoadingOverlay><JobDetailPage /></LoadingOverlay>} />
                      <Route path="product/:solutionSlug" element={<LoadingOverlay><ProductDetailPage /></LoadingOverlay>} />

                      {/* Integrations pages */}
                      <Route path="/integrations" element={<IntegrationsPage />} />
                      <Route path="/setup-integration/:integrationName" element={<IntegrationSetup />} />
                      <Route path="/edit-integration/:integrationName" element={<EditIntegration />} />
                      <Route path="/alerts" element={<IntegrationAlerts />} />
                      {/* AI Feedback pages */}
                      <Route path="ConversationReview" element={<LoadingOverlay><ConversationReview /></LoadingOverlay>} />
                      <Route path="CategorizationFeedback" element={<LoadingOverlay><TrainingCategorizationFeedbackLoopInterface /></LoadingOverlay>} />
                      <Route path="TrainingActivity" element={<LoadingOverlay><TrainingActivity /></LoadingOverlay>} />

                      {/* New AI Interview tool Routes */}
                      <Route path="/interviews/:interviewId/view" element={<InterviewViewerPage />} /> {/* The page to view the interview results */}
                      <Route path="/interviews" element={<InterviewsPage />} />

                      {/* Admin */}
                      <Route path="/admin/users" element={<Admin />} />
                      <Route path="/admin/interviews" element={<InterviewsListAdmin />} />
                      <Route path="/admin/interview-types" element={<InterviewTypesAdmin />} />                    
                      <Route path="/admin/organizations" element={<OrganizationsAdmin />} />
                      <Route path="/admin/industries" element={<IndustriesAdmin />} />
                      <Route path="/admin/products" element={<ProductsAdmin />} />
                      <Route path="/admin/interview-scripts" element={<InterviewScriptsAdmin />} />
                      <Route path="/admin/funnel" element={<AdminFunnel />} />

                    </Route>
                  </Routes>
                </Router>
              </TableVisibilityProvider>
            </DashboardDataProvider>
          </FilterProvider>
        </AuthProvider>
      </GlobalLoadingProvider>
    </LocalizationProvider>
  );
}

export default App;